import { useCallback, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Outlet, useNavigate, useSearch } from '@tanstack/react-router';

import Page from '@/components/page/Page';
import { Card } from '@/components/ui/card';
import { DataTable } from '@/components/common/data-table/data-table';
import { DataTablePagination } from '@/components/common/data-table/data-table-pagination';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import { DateRangePicker } from '@/components/common/date-pickers/date-range-picker';
import { DateRange } from 'react-day-picker';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { SystemEntityInline } from '@/components/system-entities';
import { SystemEntity } from '@erp/shared-types';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';

import { usePurchaseOrdersList } from '@/api/rpc/hooks/accounts-payable/usePurchaseOrders';
import type { PurchaseOrder } from '@erp/rpc';

type PurchaseOrderRow = PurchaseOrder;

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  SENT: 'Sent',
  PARTIALLY_RECEIVED: 'Partially Received',
  FULLY_RECEIVED: 'Fully Received',
  PARTIALLY_INVOICED: 'Partially Invoiced',
  FULLY_INVOICED: 'Fully Invoiced',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

const PO_STATUS_OPTIONS: MultiSelectOption[] = Object.entries(
  STATUS_LABELS,
).map(([id, label]) => ({ id, label }));

const STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  DRAFT: 'outline',
  PENDING_APPROVAL: 'secondary',
  APPROVED: 'default',
  SENT: 'default',
  PARTIALLY_RECEIVED: 'secondary',
  FULLY_RECEIVED: 'default',
  PARTIALLY_INVOICED: 'secondary',
  FULLY_INVOICED: 'default',
  CLOSED: 'outline',
  CANCELLED: 'destructive',
};

const columns: ColumnDef<PurchaseOrderRow>[] = [
  {
    accessorKey: 'externalId',
    header: 'PO #',
    cell: ({ getValue }) => (
      <span className="font-medium text-sm">{getValue<string>()}</span>
    ),
    size: 160,
  },
  {
    accessorKey: 'internalId',
    header: 'Internal ID',
    cell: ({ getValue }) => (
      <span className="text-sm text-muted-foreground">
        {getValue<number>()}
      </span>
    ),
    size: 120,
  },
  {
    accessorKey: 'orderDate',
    header: 'Order Date',
    cell: ({ getValue }) => {
      const date = getValue<string>();
      return (
        <span className="text-sm text-muted-foreground">
          {date ? format(new Date(date), 'MM/dd/yyyy') : '—'}
        </span>
      );
    },
    size: 140,
  },
  {
    accessorKey: 'deliveryDate',
    header: 'Delivery Date',
    cell: ({ getValue }) => {
      const date = getValue<string | null>();
      return (
        <span className="text-sm text-muted-foreground">
          {date ? format(new Date(date), 'MM/dd/yyyy') : '—'}
        </span>
      );
    },
    size: 140,
  },
  {
    accessorKey: 'totalAmount',
    header: 'Total',
    cell: ({ row }) => {
      const amount = Number(row.original.totalAmount ?? '0');
      const currency = row.original.currency || 'USD';

      try {
        const validAmount = Number.isFinite(amount) ? amount : 0;
        return (
          <span className="text-sm font-semibold">
            {validAmount.toLocaleString(undefined, {
              style: 'currency',
              currency,
              minimumFractionDigits: 2,
            })}
          </span>
        );
      } catch {
        return <span className="text-sm font-semibold">$0.00</span>;
      }
    },
    size: 160,
  },
  {
    accessorKey: 'supplierId',
    header: 'Supplier',
    cell: ({ getValue }) => {
      const supplierId = getValue<string>();
      return supplierId ? (
        <div className="max-w-[200px]">
          <SystemEntityInline
            entityType={SystemEntity.AP_SUPPLIER}
            entityId={supplierId}
          />
        </div>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue<string>();
      const label = STATUS_LABELS[status] || status;
      const variant = STATUS_VARIANTS[status] || 'outline';
      return <Badge variant={variant}>{label}</Badge>;
    },
    size: 180,
  },
];

// Search params type from route validation
type PurchaseOrdersSearchParams = {
  externalId?: string;
  supplierId?: string;
  status?: string[];
  orderDateFrom?: string;
  orderDateTo?: string;
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
  page?: number;
  pageSize?: number;
};

export function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const searchParams = useSearch({
    from: '/authenticated/accounts-payable/purchase-orders',
  }) as PurchaseOrdersSearchParams;

  // Derive filter state from URL search params
  const filters = useMemo(
    () => ({
      externalId: searchParams.externalId ?? '',
      supplierId: searchParams.supplierId ?? '',
      status: searchParams.status ?? [],
      orderDateRange:
        searchParams.orderDateFrom || searchParams.orderDateTo
          ? ({
              from: searchParams.orderDateFrom
                ? parseISO(searchParams.orderDateFrom)
                : undefined,
              to: searchParams.orderDateTo
                ? parseISO(searchParams.orderDateTo)
                : undefined,
            } as DateRange)
          : undefined,
      deliveryDateRange:
        searchParams.deliveryDateFrom || searchParams.deliveryDateTo
          ? ({
              from: searchParams.deliveryDateFrom
                ? parseISO(searchParams.deliveryDateFrom)
                : undefined,
              to: searchParams.deliveryDateTo
                ? parseISO(searchParams.deliveryDateTo)
                : undefined,
            } as DateRange)
          : undefined,
    }),
    [searchParams],
  );

  // Derive pagination state from URL search params
  const paginationState = useMemo(
    () => ({
      pageIndex: (searchParams.page ?? 1) - 1, // Convert 1-based URL to 0-based table
      pageSize: searchParams.pageSize ?? 25,
    }),
    [searchParams.page, searchParams.pageSize],
  );

  // Helper to update URL search params
  const updateSearchParams = useCallback(
    (updates: Partial<PurchaseOrdersSearchParams>, resetPage = false) => {
      navigate({
        to: '/accounts-payable/purchase-orders',
        search: {
          ...searchParams,
          ...updates,
          // Reset to page 1 when filters change
          page: resetPage ? undefined : (updates.page ?? searchParams.page),
        },
        replace: true,
      });
    },
    [navigate, searchParams],
  );

  const queryFilters = useMemo(() => {
    return {
      externalId: filters.externalId || undefined,
      supplierId: filters.supplierId || undefined,
      status: filters.status.length > 0 ? filters.status : undefined,
      orderDateFrom: searchParams.orderDateFrom,
      orderDateTo: searchParams.orderDateTo,
      deliveryDateFrom: searchParams.deliveryDateFrom,
      deliveryDateTo: searchParams.deliveryDateTo,
      limit: paginationState.pageSize,
      offset: paginationState.pageIndex * paginationState.pageSize,
    };
  }, [filters, searchParams, paginationState]);

  const { purchaseOrders, pagination, isLoading } =
    usePurchaseOrdersList(queryFilters);

  const rowCount = pagination?.total ?? 0;
  const pageCount =
    rowCount > 0 ? Math.ceil(rowCount / paginationState.pageSize) : 0;

  const table = useReactTable({
    columns,
    data: purchaseOrders,
    manualPagination: true,
    rowCount,
    state: {
      pagination: paginationState,
    },
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === 'function' ? updater(paginationState) : updater;
      updateSearchParams({
        page: newState.pageIndex + 1, // Convert 0-based table to 1-based URL
        pageSize: newState.pageSize,
      });
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const onRowClick = useCallback(
    (po: PurchaseOrderRow) => {
      navigate({
        to: '/accounts-payable/purchase-orders/$purchaseOrderId',
        params: { purchaseOrderId: po.id },
        search: true,
        viewTransition: true,
      });
    },
    [navigate],
  );

  const handleFilterChange = useCallback(
    (updates: {
      externalId?: string;
      supplierId?: string;
      status?: string[];
      orderDateFrom?: string;
      orderDateTo?: string;
      deliveryDateFrom?: string;
      deliveryDateTo?: string;
    }) => {
      updateSearchParams(updates, true); // Reset to page 1 when filters change
    },
    [updateSearchParams],
  );

  const handleOrderDateChange = useCallback(
    (dateRange: DateRange | undefined) => {
      handleFilterChange({
        orderDateFrom: dateRange?.from
          ? format(dateRange.from, 'yyyy-MM-dd')
          : undefined,
        orderDateTo: dateRange?.to
          ? format(dateRange.to, 'yyyy-MM-dd')
          : undefined,
      });
    },
    [handleFilterChange],
  );

  const handleDeliveryDateChange = useCallback(
    (dateRange: DateRange | undefined) => {
      handleFilterChange({
        deliveryDateFrom: dateRange?.from
          ? format(dateRange.from, 'yyyy-MM-dd')
          : undefined,
        deliveryDateTo: dateRange?.to
          ? format(dateRange.to, 'yyyy-MM-dd')
          : undefined,
      });
    },
    [handleFilterChange],
  );

  const handleClearFilters = useCallback(() => {
    navigate({
      to: '/accounts-payable/purchase-orders',
      search: {}, // Clear all search params
      replace: true,
    });
  }, [navigate]);

  const handlePageChange = useCallback(
    (pageIndex: number) => {
      updateSearchParams({
        page: pageIndex + 1, // Convert 0-based to 1-based
      });
    },
    [updateSearchParams],
  );

  const isFiltered = useMemo(() => {
    return Boolean(
      filters.externalId ||
        filters.supplierId ||
        filters.status.length > 0 ||
        filters.orderDateRange ||
        filters.deliveryDateRange,
    );
  }, [filters]);

  return (
    <Page title="Purchase Orders">
      <div className="space-y-4">
        <Card className="p-4 space-y-4">
          <DataTableToolbar
            table={table}
            onClearFilters={handleClearFilters}
            hasCustomFilters={isFiltered}
          >
            <Input
              placeholder="PO #"
              value={filters.externalId}
              onChange={(event) =>
                handleFilterChange({
                  externalId: event.target.value || undefined,
                })
              }
              className="h-8 w-[140px]"
            />
            <SupplierAutocomplete
              supplierId={filters.supplierId || undefined}
              onSelect={(supplierId) =>
                handleFilterChange({ supplierId: supplierId || undefined })
              }
              className="w-[200px]"
            />
            <MultiSelect
              options={PO_STATUS_OPTIONS}
              value={PO_STATUS_OPTIONS.filter((o) =>
                filters.status.includes(o.id),
              )}
              onChange={(options) => {
                const ids = options.map((o) => o.id);
                handleFilterChange({
                  status: ids.length > 0 ? ids : undefined,
                });
              }}
              placeholder="Status"
              size="sm"
              shouldAutoSort={false}
              className="h-8"
            />
            <DateRangePicker
              value={filters.orderDateRange}
              onChange={handleOrderDateChange}
              placeholder="Order date"
              className="w-fit"
            />
            <DateRangePicker
              value={filters.deliveryDateRange}
              onChange={handleDeliveryDateChange}
              placeholder="Delivery date"
              className="w-fit"
            />
          </DataTableToolbar>
        </Card>

        <Card className="ring-1 ring-border">
          <DataTable
            table={table}
            isLoading={isLoading}
            onRowClick={(row) => onRowClick(row)}
          />
          <DataTablePagination
            table={table}
            pageCount={pageCount}
            pageIndex={paginationState.pageIndex}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>

      <Outlet />
    </Page>
  );
}
